import { useState } from "react";
import { useNavigate, useSearchParams, Navigate, Link } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../lib/auth";
import { useConfig } from "../lib/config";
import { errMsg } from "../lib/api";
import { toast, NoticeRegion } from "../lib/notices";
import GoogleSignInButton from "../components/GoogleSignInButton";


export default function Register() {
  const { register } = useAuth();
  const { waitlistMode, ready } = useConfig();
  const [params] = useSearchParams();
  const inviteToken = params.get("invite") || "";
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await register(email, pw, name, inviteToken); toast.success("Account created"); nav("/dashboard"); }
    catch (err) { toast.error(errMsg(err, "Registration failed")); }
    finally { setLoading(false); }
  };

  // Google may sign in an existing account as well as create one, so it
  // gets the sign-in greeting rather than "Account created".
  const googleSignedIn = () => { toast.success("Welcome"); nav("/dashboard"); };

  // Wait for the flag before deciding — rendering the signup form and then
  // yanking it away reads as a bug.
  if (!ready) {
    return (
      <div className="min-h-screen">
        <Header />
        <main id="main" tabIndex={-1} className="max-w-md mx-auto px-6 py-16">
          <p role="status" className="font-mono text-xs uppercase tracking-widest text-ink-muted">Loading…</p>
        </main>
      </div>
    );
  }

  // Invite-only: without a token there is nothing to do here.
  if (waitlistMode && !inviteToken) return <Navigate to="/waitlist" replace />;

  return (
    <div className="min-h-screen">
      <Header />
      <main id="main" tabIndex={-1} className="max-w-md mx-auto px-6 py-16">
        <div className="marquee-tag mb-4">// AUTH</div>
        <h1 className="font-display font-black type-title mb-4 tracking-tighter">CREATE ACCOUNT.</h1>
        {inviteToken && (
          <p className="brutal-chip brutal-chip-success mb-6">Invite accepted — welcome in</p>
        )}
        {!inviteToken && <div className="mb-8" />}
        <NoticeRegion />
        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <div>
            <label className="brutal-label" htmlFor="register-name">Name</label>
            <input id="register-name" data-testid="register-name" required className="brutal-input" value={name} onChange={(e)=>setName(e.target.value)} />
          </div>
          <div>
            <label className="brutal-label" htmlFor="register-email">Email</label>
            <input id="register-email" data-testid="register-email" type="email" required className="brutal-input" value={email} onChange={(e)=>setEmail(e.target.value)} />
          </div>
          <div>
            <label className="brutal-label" htmlFor="register-password">Password (min 6)</label>
            <input id="register-password" data-testid="register-password" type="password" minLength={6} required className="brutal-input" value={pw} onChange={(e)=>setPw(e.target.value)} />
          </div>
          <button data-testid="register-submit" disabled={loading} type="submit" className="brutal-btn w-full justify-center">{loading?"Creating…":"Create Account"}</button>
        </form>
        {/* Google sign-in creates an account without touching /auth/register,
            so while sign-ups are invite-only it stays off this page (the
            backend also refuses a new uninvited account). */}
        {!waitlistMode && <GoogleSignInButton onSignedIn={googleSignedIn} testId="register-google" />}
        <p className="mt-8 text-sm text-center">
          Already have an account? <Link to="/login" className="underline font-bold" data-testid="link-login">Sign in</Link>
        </p>
      </main>
    </div>
  );
}
